import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Difficulty } from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { GameIcon } from "../../components/illustrations/GameIcon.js";
import { DifficultyInfoBox } from "../../components/game/DifficultyInfoBox.js";
import { useI18n, type TranslationKey } from "../../i18n/index.js";
import { readLastOptions, writeLastOptions } from "../../lib/lastOptions.js";
import { ROUND_TIME_SECONDS } from "../../lib/roundTime.js";
import {
  fetchCrowflightPoolSize,
  fetchCrowflightRegions,
  type CrowflightPoolSize,
  type CrowflightRegionsResponse,
} from "./api.js";
import styles from "./CrowflightStart.module.css";

export interface CrowflightOptions {
  region: string;
  difficulty: Difficulty;
}

const DIFFICULTIES: Difficulty[] = ["easy", "normal", "hard"];

const CONTINENT_LABEL_KEYS: Record<string, TranslationKey> = {
  africa: "crowflight.continent.africa",
  asia: "crowflight.continent.asia",
  europe: "crowflight.continent.europe",
  north_america: "crowflight.continent.north_america",
  south_america: "crowflight.continent.south_america",
  oceania: "crowflight.continent.oceania",
};

export function CrowflightStart({ onStart }: { onStart: (options: CrowflightOptions) => void }) {
  const { t } = useI18n();
  const saved = readLastOptions<CrowflightOptions>("crowflight");
  const [difficulty, setDifficulty] = useState<Difficulty>(saved?.difficulty ?? "normal");
  const [region, setRegion] = useState<string>(saved?.region ?? "world");
  const [regions, setRegions] = useState<CrowflightRegionsResponse | "loading" | "error">("loading");
  const [poolSize, setPoolSize] = useState<CrowflightPoolSize | "loading" | "error">("loading");

  useEffect(() => {
    setRegions("loading");
    fetchCrowflightRegions(difficulty)
      .then(setRegions)
      .catch(() => setRegions("error"));
  }, [difficulty]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    setPoolSize("loading");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCrowflightPoolSize(region, difficulty)
        .then(setPoolSize)
        .catch(() => setPoolSize("error"));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [region, difficulty]);

  const canStart = poolSize !== "loading" && poolSize !== "error" && poolSize.enough;

  function handleStart() {
    if (!canStart) return;
    const options: CrowflightOptions = { region, difficulty };
    writeLastOptions("crowflight", options);
    onStart(options);
  }

  return (
    <main className={styles.main}>
      <GameIcon gameId="crowflight" className={styles.icon} />
      <h1 className={styles.title}>{GAME_BRANDING.crowflight.name}</h1>
      <p className={styles.tagline}>{t("crowflight.tagline")}</p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t("crowflight.start.regionLabel")}</h2>
        {regions === "loading" && <p className={styles.status}>{t("common.loading")}</p>}
        {regions === "error" && <p className={styles.statusError}>{t("crowflight.start.loadError")}</p>}
        {regions !== "loading" && regions !== "error" && (
          <div className={styles.chips}>
            <button
              type="button"
              className={styles.chip}
              aria-pressed={region === "world"}
              onClick={() => setRegion("world")}
            >
              {t("crowflight.start.world")}
            </button>
            {regions.continents.map((c) => (
              <button
                key={c.continent}
                type="button"
                className={styles.chip}
                aria-pressed={region === `continent:${c.continent}`}
                onClick={() => setRegion(`continent:${c.continent}`)}
              >
                {t(CONTINENT_LABEL_KEYS[c.continent] ?? "crowflight.continent.africa")}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t("timeslip.start.difficultyLabel")}</h2>
        <div className={styles.segmented} role="group" aria-label={t("timeslip.start.difficultyLabel")}>
          {DIFFICULTIES.map((d) => (
            <button
              key={d}
              type="button"
              className={styles.segment}
              aria-pressed={difficulty === d}
              onClick={() => setDifficulty(d)}
            >
              {t(`timeslip.start.difficulty.${d}`)}
            </button>
          ))}
        </div>
        <DifficultyInfoBox
          description={t(`crowflight.start.difficultyInfo.${difficulty}`)}
          roundLimitSeconds={ROUND_TIME_SECONDS[difficulty]}
          noLimitLabel={t("common.noTimeLimit")}
        />
      </section>

      {poolSize !== "loading" && poolSize !== "error" && !poolSize.enough && (
        <p className={styles.hint}>{t("timeslip.start.notEnoughCards")}</p>
      )}

      <button type="button" className={styles.startButton} disabled={!canStart} onClick={handleStart}>
        {t("timeslip.start.startButton")}
      </button>
      <Link to="/" className={styles.backLink}>
        ← {t("common.backHome")}
      </Link>
    </main>
  );
}
